import React, { FC } from 'react';

import { Teaser } from '../Teaser/Teaser';

import { HeadersWithDescription } from '../HeadersWithDescription/HeadersWithDescription';

import {
    ContentfulNavigationLinkExternal,
    ContentfulNavigationLinkInternal,
    GenericGridContainerIndent,
    GenericGridElementTypes,
    GenericGridHorizontalAlignTypes,
    GenericGridRatioType,
    GenericGridVerticalAlignTypes,
} from '../../contentful/types';

import { NavigationLink } from '../Header/Navigation/NavigationLink/NavigationLink';

import * as styles from './GenericGrid.module.scss';

import { GenericGridProps } from './GenericGrid.types';

const getHorizontalAlignmentClass = (horizontalAlign: GenericGridHorizontalAlignTypes) => {
    const alignmentMapping: Record<GenericGridHorizontalAlignTypes, string> = {
        center: styles.horizontalAlignCenter,
        right: styles.horizontalAlignRight,
        left: styles.horizontalAlignLeft,
    };

    return alignmentMapping[horizontalAlign] || styles.horizontalAlignLeft;
};

const getVerticalAlignmentClass = (horizontalAlign: GenericGridVerticalAlignTypes) => {
    const alignmentMapping: Record<GenericGridVerticalAlignTypes, string> = {
        top: styles.verticalAlignTop,
        center: styles.verticalAlignCenter,
        bottom: styles.verticalAlignBottom,
    };

    return alignmentMapping[horizontalAlign] || styles.verticalAlignTop;
};

const getRatio = (ratio: GenericGridRatioType = '1-1'): string => {
    return styles[`ratio${ratio?.replace(/-/g, '')}`] ?? '11';
};

const renderElement = (el: GenericGridElementTypes, index: number) => {
    if (el.__typename === 'ContentfulHeadersWithDescription') {
        return (
            <div key={`${el.contentful_id}--${index}`} className={styles.gridItem}>
                <HeadersWithDescription
                    subtitle={el.subtitle}
                    subtitleSize={el.subtitleSize}
                    title={el.headerTitle}
                    titleSize={el.titleSize}
                    description={el.description}
                    descriptionSize={el.descriptionSize}
                    contentAlignment={el.contentAlignment}
                />
            </div>
        );
    } else if (el.__typename === 'ContentfulGenericNavigationLink') {
        const item: ContentfulNavigationLinkExternal | ContentfulNavigationLinkInternal = {
            __typename: el.__typename,
            contentful_id: el.contentful_id,
            title: el.title,
            linkIcon: el.linkIcon,
            buttonType: el.buttonType,
            ...('url' in el && el.url ? { url: el.url } : {}),
            ...('pageReference' in el && el.pageReference ? { pageReference: el.pageReference } : {}),
        };

        return (
            <div key={`${el.contentful_id}--${index}`} className={`${styles.gridItem}`}>
                <NavigationLink item={item} variant={'featured'} inverted={false} />
            </div>
        );
    }
};

const getIndentClass = (containerIndent: GenericGridContainerIndent) => {
    const indentMapping: Record<GenericGridContainerIndent, string> = {
        none: '',
        small: styles.indentSmall,
        medium: styles.indentMedium,
        large: styles.indentLarge,
    };

    return indentMapping[containerIndent] || '';
};
export const GenericGrid: FC<GenericGridProps> = ({
    title,
    elements,
    elementsPerRow,
    horizontalAlign,
    verticalAlign,
    ratio = '1-1',
    containerIndent = 'none',
}) => {
    return (
        <div className={getIndentClass(containerIndent)}>
            {title && <Teaser text={title} />}
            <div
                className={[
                    styles[`grid${elementsPerRow}`],
                    getHorizontalAlignmentClass(horizontalAlign),
                    getRatio(ratio),
                    getVerticalAlignmentClass(verticalAlign),
                ].join(' ')}
            >
                {elements?.map(renderElement)}
            </div>
        </div>
    );
};
